/**
 * Check-out Screen — vehicle return inspection with check-in comparison.
 *
 * Same inspection form as check-in but with:
 * - ComparisonRows showing check-in vs current values
 * - Extra km charge and late fee calculations
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { View, Text, TextInput as RNTextInput } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { useBooking } from '@/lib/hooks/use-booking'
import { ServiceError } from '@/lib/errors/service-error'
import { useInspection, useCreateInspection } from '@/lib/hooks/use-inspection'
import { HandoverPresenceCard } from '@/components/trip/HandoverPresenceCard'
import { inspectionFormSchema } from '@/lib/validations/inspection.schema'
import { useCheckinDraftStore } from '@/lib/stores/checkin.store'
import { useLateFeePreview } from '@/lib/hooks/use-late-fee-preview'
import { etapaDeInspeccion, tituloDeEtapa, cuerpoDeEtapa } from '@/lib/utils/inspection-stage'
import { sanitizeOdometerText } from '@/lib/utils/odometer-input'
import {
  canPersistInspectionDraft,
  hydrateInspectionDraft,
} from '@/lib/utils/inspection-draft-hydration'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { formatCurrency } from '@/lib/utils/format'
import { formatBookingDateTime } from '@/lib/utils/dates'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { SignaturePad } from '@/components/ui/SignaturePad'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { FuelLevelSlider } from '@/components/trip/FuelLevelSlider'
import { ConditionPicker } from '@/components/trip/ConditionPicker'
import { ComparisonRow } from '@/components/trip/ComparisonRow'
import { InspectionPhotoGrid } from '@/components/trip/InspectionPhotoGrid'
import { InspectionReadinessCard } from '@/components/trip/InspectionReadinessCard'
import {
  INSPECTION_PHOTO_AREAS,
  MIN_INSPECTION_PHOTOS,
  faltanAreasObligatorias,
  type ConditionGrade,
  type InspectionPhoto,
  type InspectionPhotoArea,
} from '@/lib/types/inspection.types'

/**
 * Distinguir "no existe" de "no llego".
 *
 * `getBookingDetail` LANZA en los dos casos: `maybeSingle()` con cero filas
 * devuelve `data: null` sin error, y el servicio convierte eso en
 * `ServiceError.notFound`. O sea que `bookingError` esta puesto tambien cuando
 * la reserva simplemente no existe -- y una rama que solo mire "hay error"
 * manda TODO al muro de transporte, dejando muerto el otro.
 *
 * Me lo encontro una revision de mi propia compuerta: separe tres hechos y el
 * tercero era inalcanzable. El discriminador es el codigo, no la presencia del
 * error.
 */
/**
 * El error de subida, dicho con nombres de fotos.
 *
 * `INSPECTION_PHOTOS_FAILED` viaja con `details.failedAreas`, y hasta hoy eso
 * no llegaba a la pantalla: se mostraba "No se pudo completar la entrega" y la
 * persona no tenia forma de saber CUAL de las dieciseis fotos falto. Con quince
 * angulos identicos en una cuadricula, eso es imposible de adivinar.
 *
 * Y ahora se puede prometer sin mentir que reintentar es barato: la subida es
 * idempotente por area y las que ya estan arriba no se vuelven a subir.
 */
function mensajeDeFotosFallidas(
  err: unknown,
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string | null {
  if (!(err instanceof ServiceError) || err.code !== 'INSPECTION_PHOTOS_FAILED') return null
  const detalles = err.details as { failedAreas?: unknown } | undefined
  const areas = Array.isArray(detalles?.failedAreas)
    ? detalles.failedAreas.filter((a): a is string => typeof a === 'string')
    : []
  if (areas.length === 0) return null
  return t('checkin.photos_failed_body', {
    areas: areas.map((area) => t(`checkin.photo_${area}`)).join(', '),
  })
}

function esReservaInexistente(err: unknown): boolean {
  return err instanceof ServiceError && err.code === 'NOT_FOUND'
}

export default function CheckoutScreen() {
  const { bookingId } = useLocalSearchParams<{ bookingId: string }>()
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { user } = useAuth()

  // La entrega, para comparar contra ella.
  const {
    data: checkinData,
    isLoading: isCheckinLoading,
    isError: isCheckinError,
    refetch: refetchCheckin,
  } = useInspection(bookingId, 'checkin')
  const {
    data: booking,
    isLoading: bookingLoading,
    error: bookingError,
    refetch: refetchBooking,
  } = useBooking(bookingId)

  const {
    mutateAsync: createInspection,
    isPending,
    photoProgress,
    totalPhotos,
  } = useCreateInspection()

  /*
   * Un error de devolución debe quedarse para poder leerse, pero no puede
   * viajar con una devolución que luego sí funcionó ni cubrir su pantalla de
   * detalle. Este id limita la limpieza al aviso creado por este formulario.
   */
  const handoverErrorToastIdRef = useRef<string | null>(null)
  const dismissHandoverError = useCallback(() => {
    if (!handoverErrorToastIdRef.current) return
    toast.dismiss(handoverErrorToastIdRef.current)
    handoverErrorToastIdRef.current = null
  }, [])
  const showHandoverError = useCallback(
    (message: string, description?: string) => {
      dismissHandoverError()
      handoverErrorToastIdRef.current = toast.error(
        message,
        description ? { description } : undefined,
      )
    },
    [dismissHandoverError],
  )

  useEffect(() => () => dismissHandoverError(), [dismissHandoverError])

  // ─── Form State ─────────────────────────────────────────────────────────
  const [fuelLevel, setFuelLevel] = useState(50)
  // Mismo motivo que en la entrega: el 50% inicial es una posicion del slider,
  // no una lectura del tablero. Nadie afirmo nada hasta que alguien lo mueve.
  const [fuelTouched, setFuelTouched] = useState(false)
  const [odometerText, setOdometerText] = useState('')
  const odometerInputRef = useRef<RNTextInput>(null)
  const [exteriorCondition, setExteriorCondition] = useState<ConditionGrade | undefined>(undefined)
  const [interiorCondition, setInteriorCondition] = useState<ConditionGrade | undefined>(undefined)
  const [notes, setNotes] = useState('')
  const [photos, setPhotos] = useState<InspectionPhoto[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  /*
   * Las dos firmas de la entrega.
   *
   * Una entrega de carro es un acto entre dos personas y hasta hoy solo la
   * registraba una: quien sostenía el teléfono. Las columnas existían desde el
   * principio con cero filas escritas.
   *
   * Ambas en el mismo dispositivo porque así ocurre el hecho: anfitrión y
   * huésped están parados junto al carro. Pedir dos sesiones sería inventar una
   * ceremonia que la situación real no tiene.
   */
  const [guestSignature, setGuestSignature] = useState<string | null>(null)
  const [hostSignature, setHostSignature] = useState<string | null>(null)

  /*
   * EL BORRADOR, TAMBIEN ACA.
   *
   * La entrega ya sobrevivia a que Android matara la app; la devolucion no. Era
   * una asimetria sin razon: son el mismo formulario, con las mismas quince
   * fotos y las mismas dos firmas, hecho en la misma calle. El store ya esta
   * llaveado por (renta, tipo), asi que la devolucion no pisa la entrega.
   */
  const { getDraft, saveDraft, clearDraft } = useCheckinDraftStore()
  const [hydratedBookingId, setHydratedBookingId] = useState<string | null>(null)

  useEffect(() => {
    if (!bookingId || hydratedBookingId === bookingId) return

    const borrador = hydrateInspectionDraft(getDraft(bookingId, 'checkout'))
    setFuelLevel(borrador.fuelLevel)
    setFuelTouched(borrador.fuelTouched)
    setOdometerText(borrador.odometerText)
    setExteriorCondition(borrador.exteriorCondition)
    setInteriorCondition(borrador.interiorCondition)
    setNotes(borrador.notes)
    setPhotos(borrador.photos)
    setGuestSignature(borrador.guestSignature)
    setHostSignature(borrador.hostSignature)
    setErrors({})
    setHydratedBookingId(bookingId)
  }, [bookingId, getDraft, hydratedBookingId])

  useEffect(() => {
    if (!canPersistInspectionDraft(bookingId, hydratedBookingId)) return
    saveDraft(bookingId, 'checkout', {
      fuelLevel,
      fuelTouched,
      odometerText,
      exteriorCondition,
      interiorCondition,
      notes,
      photos,
      guestSignature,
      hostSignature,
    })
  }, [
    bookingId,
    exteriorCondition,
    fuelLevel,
    fuelTouched,
    guestSignature,
    hydratedBookingId,
    hostSignature,
    interiorCondition,
    notes,
    odometerText,
    photos,
    saveDraft,
  ])

  const etapa = etapaDeInspeccion(booking?.status, 'checkout')
  /*
   * EL RECARGO POR TARDANZA, DICHO ANTES DE COBRARLO.
   *
   * `trg_apply_late_fee_on_completion` lo cobra de verdad al cerrar el viaje y
   * lo suma al pago del anfitrion. La app no lo mencionaba en ningun lado:
   * alguien devolvia dos horas tarde y se enteraba despues. El numero sale de
   * la MISMA funcion de la base que lo cobra, no de una tercera cuenta hecha
   * aca.
   */
  const { data: mora } = useLateFeePreview(bookingId)

  const odometerKm = odometerText ? parseInt(odometerText, 10) : 0
  /*
   * Lo que decide es el CONJUNTO de areas, no cuantas fotos hay.
   *
   * Con quince obligatorias de quince areas, contar y tener eran lo mismo. Al
   * bajar a seis dejaron de serlo: contar sola aceptaria seis fotos de asientos
   * y cero de la carroceria. La cuenta sigue viva solo para la barra.
   */
  const areasObligatoriasFaltantes = faltanAreasObligatorias(photos.map((foto) => foto.area))
  const hasEnoughPhotos = areasObligatoriasFaltantes.length === 0

  // ─── Comparison Computations ────────────────────────────────────────────
  const checkinFuel = checkinData?.fuelLevel ?? 0
  const checkinOdometer = checkinData?.odometerKm ?? 0
  const hasCheckinBaseline = Boolean(checkinData)
  const fuelDifference = fuelLevel - checkinFuel
  const odometerBelowCheckin = hasCheckinBaseline && odometerKm > 0 && odometerKm < checkinOdometer
  const kmDriven = odometerKm > 0 ? Math.max(0, odometerKm - checkinOdometer) : 0
  const isFormValid =
    fuelTouched &&
    hasCheckinBaseline &&
    !odometerBelowCheckin &&
    exteriorCondition !== undefined &&
    interiorCondition !== undefined &&
    odometerText.length > 0 &&
    hasEnoughPhotos &&
    guestSignature !== null &&
    hostSignature !== null
  const readinessItems = [
    {
      key: 'fuel',
      label: t('checkin.fuel_level'),
      complete: fuelTouched,
      detail: fuelTouched ? `${fuelLevel}%` : undefined,
    },
    {
      key: 'baseline',
      label: t('trip_checkout.comparison_title'),
      complete: hasCheckinBaseline && !isCheckinLoading && !isCheckinError,
    },
    {
      key: 'odometer',
      label: t('checkin.odometer'),
      complete: odometerText.length > 0 && !odometerBelowCheckin,
      detail: odometerBelowCheckin
        ? t('trip_checkout.odometer_below_checkin', {
            km: checkinOdometer.toLocaleString(),
          })
        : undefined,
    },
    {
      key: 'exterior',
      label: t('checkin.exterior_condition'),
      complete: exteriorCondition !== undefined,
    },
    {
      key: 'interior',
      label: t('checkin.interior_condition'),
      complete: interiorCondition !== undefined,
    },
    {
      key: 'photos',
      label: t('checkin.photos_title'),
      complete: hasEnoughPhotos,
      detail: t('checkin.photos_progress', {
        count: MIN_INSPECTION_PHOTOS - areasObligatoriasFaltantes.length,
        total: MIN_INSPECTION_PHOTOS,
      }),
    },

    {
      key: 'signatures',
      label: t('inspection.signatures_title'),
      complete: guestSignature !== null && hostSignature !== null,
    },
  ]

  /*
   * LOS KILOMETROS EXTRA YA NO SE COBRAN, ASI QUE YA NO SE ANUNCIAN.
   *
   * Aca vivia un limite de 200 km/dia que NADIE pacto --un literal de esta
   * pantalla, no un dato de la reserva-- y encima un cargo en dolares que se le
   * mostraba al huesped en el momento de devolver el carro.
   *
   * Ese cargo no existe en ninguna otra parte del sistema: no hay columna en
   * `rentals`, no hay funcion en la base que lo calcule, y el ledger --que tiene
   * dieciseis tipos de asiento, incluidos `late_fee`, `cleaning_fee` y
   * `fuel_charge`-- no tiene ninguno para kilometraje. O sea que la pantalla
   * prometia un cobro que nada cobraba: la peor clase de numero, porque asusta
   * a quien lo lee y no le sirve a nadie.
   *
   * Decision del dueno, textual: "los kilometros extra no deberian de importar".
   *
   * Los kilometros RECORRIDOS si se siguen mostrando: eso es un hecho medido
   * entre dos odometros, util para las dos partes, y no promete nada.
   */

  // ─── Handlers ───────────────────────────────────────────────────────────

  /**
   * Marcar o desmarcar que una foto muestra un daño.
   *
   * Se alterna sobre la foto ya tomada en vez de preguntarlo aparte: la
   * afirmación y su evidencia son la misma cosa, y separarlas obliga a describir
   * con palabras lo que la foto ya muestra.
   */
  const handleToggleDamage = useCallback((area: InspectionPhotoArea) => {
    setPhotos((prev) => prev.map((p) => (p.area === area ? { ...p, isDamage: !p.isDamage } : p)))
  }, [])

  const handlePhotoTaken = useCallback((photo: InspectionPhoto) => {
    setPhotos((prev) => {
      /*
       * REPETIR UNA FOTO NO BORRA LO QUE SE AFIRMO SOBRE ELLA.
       *
       * Antes se descartaba la foto vieja entera y se metia la nueva, asi que
       * la marca de dano se perdia en silencio. Y repetir es justo lo que hace
       * alguien que quiere que el golpe se vea MEJOR: marca el dano, la foto le
       * queda oscura, la vuelve a tomar, y la marca desaparece sin decir nada.
       * El resultado es evidencia de un dano que el sistema cree que nadie
       * reporto.
       *
       * La foto cambia; la afirmacion sobre esa parte del carro se conserva
       * salvo que la nueva captura traiga la suya.
       */
      const anterior = prev.find((p) => p.area === photo.area)
      const resto = prev.filter((p) => p.area !== photo.area)
      return [...resto, { ...photo, isDamage: photo.isDamage ?? anterior?.isDamage }]
    })
  }, [])

  const handleSubmit = async () => {
    // Nunca terminar en silencio: un boton habilitado que no hace nada visible
    // se lee como una app rota.
    if (!bookingId) return
    if (!user) {
      void notification(NotificationType.Error)
      showHandoverError(t('inspection.session_needed_title'), t('inspection.session_needed_body'))
      return
    }
    if (!checkinData) {
      void notification(NotificationType.Error)
      showHandoverError(t('trip_checkout.checkin_missing_body'))
      return
    }
    if (odometerBelowCheckin) {
      const message = t('trip_checkout.odometer_below_checkin', {
        km: checkinOdometer.toLocaleString(),
      })
      setErrors((prev) => ({ ...prev, odometerKm: message }))
      void notification(NotificationType.Error)
      showHandoverError(message)
      return
    }

    const formData = {
      fuelLevel,
      odometerKm,
      exteriorCondition: exteriorCondition!,
      interiorCondition: interiorCondition!,
      notes,
      photos,
      guestSignatureSvg: guestSignature,
      hostSignatureSvg: hostSignature,
    }

    // Validate
    const result = inspectionFormSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const err of result.error.issues) {
        const key = err.path[0]
        if (typeof key === 'string') fieldErrors[key] = err.message
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      // Y SE DICE. El error de `photos` no tenia donde pintarse, asi que el
      // boton se veia habilitado y al tocarlo no pasaba nada.
      const primera = result.error.issues[0]
      showHandoverError(
        t('checkin.error_incomplete_title'),
        primera ? t(primera.message) : t('checkin.error_incomplete_body'),
      )
      return
    }

    setErrors({})

    try {
      await createInspection({
        rentalId: bookingId,
        userId: user.id,
        type: 'checkout',
        formData,
      })

      // El borrador se descarta solo cuando ya esta en el servidor.
      clearDraft(bookingId, 'checkout')
      void notification(NotificationType.Success)
      dismissHandoverError()
      toast.success(t('trip_checkout.success_title'), {
        description: t('trip_checkout.success_desc'),
      })
      router.replace(`/trips/${bookingId}` as never)
    } catch (err) {
      // Same observability fix as checkin/[bookingId].tsx:
      // surface real ServiceError reason and capture to Sentry instead
      // of swallowing the error behind the generic "checkout failed" toast.
      void notification(NotificationType.Error)
      // Si fallaron fotos concretas, se nombran. Si no, el motivo real.
      showHandoverError(
        mensajeDeFotosFallidas(err, t) ?? errorMessage(err, t('trip_checkout.error_failed')),
      )
      captureError(err, { screen: 'checkin/checkout', action: 'create-inspection' })
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  // TextInput ships its own FORM_FIELD_GAP bottom margin so stacked forms get
  // the right rhythm without a wrapper. This ScrollView already spaces its
  // children by SPACING.lg, so the two stacked into a 56pt hole under the
  // kilometraje field while every other step sat at 24pt. Cancelling the
  // intrinsic margin leaves the ScrollView's one gap governing the form.
  const cancelIntrinsicFieldGap = { marginBottom: -FORM_FIELD_GAP } as const

  /*
   * NO SE PIDEN 15 FOTOS PARA UNA RESERVA QUE NO EXISTE.
   *
   * Esta pantalla tomaba el `bookingId` de la URL y dibujaba el formulario
   * ENTERO sin preguntar por la reserva: nivel de combustible, kilometraje,
   * estado exterior e interior, quince fotos en vivo y una firma. Recien al
   * enviar el servidor decia que no. Con una reserva ajena o borrada eso es
   * pedirle a alguien diez minutos de trabajo para tirarlos.
   *
   * FALLA ABIERTO: mientras la consulta no resuelva no se muestra el muro. A
   * quien SI tiene la reserva no se le corta la inspeccion porque su red este
   * lenta -- y para eso existe la rama de carga de abajo.
   */
  /*
   * TRES HECHOS DISTINTOS, NO DOS.
   *
   * Mi primera version decia `if (!bookingLoading && (bookingError || !booking))`
   * y pintaba "Esta reserva ya no esta disponible". Eso FALLA CERRADO, que es lo
   * contrario de lo que este repo exige -- y en la peor pantalla posible: la
   * inspeccion se hace en la calle, junto al carro, con dos barras de senal.
   * `useBooking` tiene `retry: false`, y `getBookingDetail` lanza IGUAL por
   * reserva inexistente, por RLS y por un timeout de transporte. O sea que un
   * paquete perdido le decia a un anfitrion legitimo que la reserva "puede que
   * se haya cancelado, vencido o que no sea tuya", sin boton de reintentar, y su
   * unica salida lo sacaba de la entrega.
   *
   * Es el mismo defecto que `explore/[vehicleId].tsx` ya habia arreglado --"un
   * fallo de transporte y un carro que de verdad no esta son hechos distintos"--
   * reintroducido por mi. Ahora se separan:
   *
   *   1. HAY RESERVA EN CACHE -> nunca se levanta un muro, aunque `bookingError`
   *      este puesto. Un refetch de fondo que falla no puede borrar una fila que
   *      ya tenemos ni tapar una inspeccion a medio llenar.
   *   2. FALLO DE TRANSPORTE y sin cache -> se dice que no se pudo cargar y se
   *      ofrece REINTENTAR. No se afirma nada sobre la reserva.
   *   3. EL SERVIDOR CONTESTO Y NO HAY RESERVA -> ahi si, ya no esta disponible.
   */
  if (!bookingLoading && !booking && bookingError && !esReservaInexistente(bookingError)) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('trip_checkout.title')}
          backTestID="checkout.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID="checkout.not-available-unreachable"
            unavailable
            title={t('common.error_title')}
            description={t('common.error_description')}
            action={{ label: t('common.retry'), onPress: () => void refetchBooking() }}
            secondaryAction={{
              label: t('inspection.not_available_action'),
              onPress: () => router.replace('/(tabs)/trips' as never),
            }}
          />
        </View>
      </View>
    )
  }

  /*
   * NI 15 FOTOS NI DOS FIRMAS SOBRE UNA RENTA QUE NO LAS ADMITE.
   *
   * En la devolucion importa el doble: para llegar a 'completed' la base exige
   * que exista la inspeccion de ENTREGA. Sin esta compuerta, alguien podia
   * llenar la devolucion completa de una renta que nunca paso por la entrega y
   * estrellarse al final, despues de subir quince fotos desde la calle.
   *
   * Falla abierto: solo detiene sobre estados conocidos.
   */
  if (booking && etapa !== 'permitida') {
    const irAEntrega = etapa === 'todavia_no' && booking.status !== 'pending_payment'
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('trip_checkout.title')}
          backTestID="checkout.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID={`checkout.stage-${etapa}`}
            unavailable={etapa === 'cancelada' || etapa === 'terminada'}
            title={t(tituloDeEtapa(etapa, 'checkout'))}
            description={t(cuerpoDeEtapa(etapa, 'checkout'))}
            action={{
              label: irAEntrega ? t('checkin.confirm_checkin') : t('inspection.stage.go_to_trip'),
              onPress: () =>
                router.replace(
                  (irAEntrega ? `/checkin/${bookingId}` : `/trips/${bookingId}`) as never,
                ),
            }}
          />
        </View>
      </View>
    )
  }

  if (!bookingLoading && !booking) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        <ScreenHeader
          title={t('trip_checkout.title')}
          backTestID="checkout.back"
          style={{ paddingTop: insets.top + SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            testID="checkout.not-available"
            unavailable
            title={t('inspection.not_available_title')}
            description={t('inspection.not_available_body')}
            action={{
              label: t('inspection.not_available_action'),
              onPress: () => router.replace('/(tabs)/trips' as never),
            }}
          />
        </View>
      </View>
    )
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <ScreenHeader
        title={t('trip_checkout.title')}
        subtitle={t('trip_checkout.subtitle')}
        backTestID="checkout.back"
        style={{ paddingTop: insets.top + SPACING.sm }}
      />

      {/* Form */}
      <KeyboardAwareView
        includeTopInset={false}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: SPACING.md,
          paddingBottom: insets.bottom + SPACING['2xl'] + 80,
          gap: SPACING.lg,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {(isCheckinLoading || isCheckinError || !checkinData) && (
          <InspectionNotice
            title={
              isCheckinLoading
                ? t('trip_checkout.checkin_loading_title')
                : t('trip_checkout.checkin_missing_title')
            }
            body={
              isCheckinLoading
                ? t('trip_checkout.checkin_loading_body')
                : t('trip_checkout.checkin_missing_body')
            }
            tone={isCheckinError ? 'error' : 'warning'}
            colors={colors}
            onRetry={isCheckinError ? () => void refetchCheckin() : undefined}
          />
        )}

        {/*
          FUERA DE LA TARJETA DE COMPARACION, y esa es la correccion.

          Estaba DENTRO del bloque `{checkinData && (...)}`, asi que a quien no
          tuviera inspeccion de entrega registrada no se le decia NADA del
          recargo -- justo el caso de un traspaso que se hizo por fuera de la
          app, que es como ocurrio el primero de verdad. Y la mora no depende de
          que exista una entrega: depende del reloj.

          Lo encontro la prueba E2E nueva, no yo.
        */}
        {/*
            EL RECARGO POR TARDANZA, DICHO ANTES DE COBRARLO.

            La base lo cobra de verdad --`trg_apply_late_fee_on_completion`
            escribe `late_fee_cents` y lo suma al pago del anfitrion-- y la app
            no lo mencionaba en ninguna pantalla. Alguien devolvia dos horas
            tarde y se enteraba despues: con razon lo leia como un cobro
            sorpresa.

            El numero sale de la MISMA funcion de la base que lo cobra, no de
            una cuenta hecha aca. Y se muestra en los tres momentos que
            importan: todavia a tiempo, dentro de la cortesia, y ya con
            recargo.
          */}
        {mora ? (
          <View
            style={{
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: colors.surfaceSubtle,
              gap: SPACING.xs,
            }}
            testID="checkout.late-fee"
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 14,
                color: mora.feeCents > 0 ? colors.warning : colors.textPrimary,
              }}
            >
              {mora.feeCents > 0
                ? t('trip_checkout.late_notice_title')
                : mora.minutesLate > 0
                  ? t('trip_checkout.late_grace_title')
                  : t('trip_checkout.late_due_title', {
                      time: formatBookingDateTime(mora.endAt, i18n.language),
                    })}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                color: colors.textSecondary,
              }}
            >
              {mora.feeCents > 0
                ? t('trip_checkout.late_notice_body', {
                    amount: formatCurrency(mora.feeCents),
                    time: formatBookingDateTime(mora.endAt, i18n.language),
                  })
                : mora.minutesLate > 0
                  ? t('trip_checkout.late_grace_body', {
                      time: formatBookingDateTime(mora.graceEndsAt, i18n.language),
                      amount: formatCurrency(mora.feeAfterGraceCents),
                    })
                  : t('trip_checkout.late_due_body', {
                      grace: formatBookingDateTime(mora.graceEndsAt, i18n.language),
                      amount: formatCurrency(mora.feeAfterGraceCents),
                    })}
            </Text>
          </View>
        ) : null}

        {/* Comparison Section */}
        {checkinData && (
          <View
            style={{
              backgroundColor: colors.surfaceSubtle,
              borderRadius: RADIUS.md,
              padding: SPACING.md,
              gap: SPACING.xs,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
              {t('trip_checkout.comparison_title')}
            </Text>

            <ComparisonRow
              label={t('checkin.fuel_level')}
              checkinValue={`${checkinFuel}%`}
              checkoutValue={`${fuelLevel}%`}
              isWorse={fuelDifference < 0}
            />

            <ComparisonRow
              label={t('checkin.odometer')}
              checkinValue={checkinOdometer.toLocaleString()}
              checkoutValue={odometerKm > 0 ? odometerKm.toLocaleString() : '-'}
              unit="km"
            />

            {kmDriven > 0 && (
              <ComparisonRow
                label={t('trip_checkout.km_driven')}
                checkinValue=""
                checkoutValue={kmDriven.toLocaleString()}
                unit="km"
              />
            )}
          </View>
        )}

        {/* Fuel Level */}
        <View style={{ gap: SPACING.xs }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
            {t('checkin.fuel_level')}
          </Text>
          <FuelLevelSlider
            value={fuelLevel}
            onChange={(v) => {
              setFuelTouched(true)
              setFuelLevel(v)
            }}
            testIDPrefix="checkout"
          />
          {!fuelTouched && (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
              {t('checkin.fuel_confirm_hint')}
            </Text>
          )}
        </View>

        {/* Odometer */}
        <View style={cancelIntrinsicFieldGap}>
          <TextInput
            label={t('checkin.odometer')}
            placeholder={t('checkin.odometer_placeholder')}
            keyboardType="number-pad"
            value={odometerText}
            ref={odometerInputRef}
            onChangeText={(text) => {
              setOdometerText(sanitizeOdometerText(text, odometerInputRef.current))
              if (errors.odometerKm) {
                setErrors((prev) => {
                  const next = { ...prev }
                  delete next.odometerKm
                  return next
                })
              }
            }}
            error={
              odometerBelowCheckin
                ? t('trip_checkout.odometer_below_checkin', {
                    km: checkinOdometer.toLocaleString(),
                  })
                : errors.odometerKm
                  ? t(errors.odometerKm)
                  : undefined
            }
            testID="checkout.odometer-input"
          />
        </View>

        {/* Exterior Condition */}
        <ConditionPicker
          label={t('checkin.exterior_condition')}
          value={exteriorCondition}
          onChange={setExteriorCondition}
          testIDPrefix="checkout"
        />

        {/* Interior Condition */}
        <ConditionPicker
          label={t('checkin.interior_condition')}
          value={interiorCondition}
          onChange={setInteriorCondition}
          testIDPrefix="checkout"
        />

        {/* Photos */}
        <View style={{ gap: SPACING.sm }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
            {t('checkin.photos_title')}
          </Text>
          <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
            {t('checkin.photos_subtitle')}
          </Text>
          {!hasEnoughPhotos && (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.warning }}>
              {t('checkin.missing_required_photos', {
                areas: areasObligatoriasFaltantes
                  .map((area: InspectionPhotoArea) => t(`checkin.photo_${area}`))
                  .join(', '),
              })}
            </Text>
          )}
          <InspectionPhotoGrid
            photos={photos}
            onPhotoTaken={handlePhotoTaken}
            onToggleDamage={handleToggleDamage}
            areas={INSPECTION_PHOTO_AREAS}
            testIDPrefix="checkout"
          />
        </View>

        {/* Notes */}
        <View style={cancelIntrinsicFieldGap}>
          <TextInput
            label={t('checkin.notes')}
            placeholder={t('checkin.notes_placeholder')}
            multiline
            numberOfLines={4}
            value={notes}
            onChangeText={(text) => setNotes(text.slice(0, 500))}
            error={errors.notes ? t(errors.notes) : undefined}
            testID="checkout.notes-input"
            style={{ minHeight: 100, textAlignVertical: 'top' }}
          />
        </View>

        {/* Firmas — el acto que faltaba.
            Van DESPUÉS de las fotos y las condiciones a propósito: se firma lo
            que ya se revisó, no un formulario en blanco. */}
        <View style={{ gap: SPACING.md }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('inspection.signatures_title')}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textSecondary,
              marginTop: -SPACING.xs,
            }}
          >
            {t('inspection.signatures_help')}
          </Text>

          {/* `value` no es decoracion: sin el, un borrador recuperado dejaba
              los recuadros EN BLANCO diciendo que ya estaban firmados, y tocarlos
              borraba la firma guardada. */}
          <HandoverPresenceCard
            rentalId={booking?.id ?? bookingId}
            inspectionType="dropoff"
            isGuest={Boolean(user?.id) && booking?.guestId === user?.id}
          />
          <SignaturePad
            label={t('inspection.signature_host')}
            value={hostSignature}
            onChange={setHostSignature}
            testID="checkout.signature-host"
          />
          <SignaturePad
            label={t('inspection.signature_guest')}
            value={guestSignature}
            onChange={setGuestSignature}
            testID="checkout.signature-guest"
          />
        </View>

        <InspectionReadinessCard
          title={isFormValid ? t('trip_checkout.ready_title') : t('checkin.readiness_title')}
          items={readinessItems}
          testID="checkout.readiness"
        />
      </KeyboardAwareView>

      {/* Bottom CTA */}
      <View
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          paddingHorizontal: SPACING.md,
          paddingBottom: insets.bottom + SPACING.md,
          paddingTop: SPACING.md,
          backgroundColor: colors.background,
          borderTopWidth: 1,
          borderTopColor: colors.border,
        }}
      >
        {/* El progreso vive junto al boton, no al final de un formulario
            largo: una espera sin cifra se siente como una app colgada, y de ahi
            salen los toques repetidos sobre una accion irreversible. */}
        {isPending && totalPhotos > 0 ? (
          <View style={{ gap: SPACING.xs, marginBottom: SPACING.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
                numberOfLines={1}
              >
                {t('checkin.uploading_photos')}
              </Text>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}>
                {`${photoProgress}/${totalPhotos}`}
              </Text>
            </View>
            <View
              style={{
                height: 6,
                borderRadius: RADIUS.full,
                backgroundColor: colors.surfaceSubtle,
                overflow: 'hidden',
              }}
              accessibilityRole="progressbar"
              accessibilityLabel={t('checkin.uploading_photos')}
              accessibilityValue={{ min: 0, max: totalPhotos, now: photoProgress }}
              testID="checkout.upload-progress"
            >
              <View
                style={{
                  height: '100%',
                  width: `${Math.round((photoProgress / Math.max(totalPhotos, 1)) * 100)}%`,
                  backgroundColor: colors.textAccent,
                }}
              />
            </View>
          </View>
        ) : null}
        <Button
          title={t('trip_checkout.confirm_checkout')}
          onPress={() => void handleSubmit()}
          loading={isPending}
          disabled={!isFormValid || isPending}
          testID="checkout.confirm-button"
        />
      </View>
    </View>
  )
}

function InspectionNotice({
  title,
  body,
  tone,
  colors,
  onRetry,
}: {
  title: string
  body: string
  tone: 'warning' | 'error'
  colors: ReturnType<typeof useSemanticColors>
  onRetry?: () => void
}) {
  const accent = tone === 'error' ? colors.error : colors.warning
  const { t } = useTranslation()

  return (
    <View
      style={{
        backgroundColor: `${accent}12`,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: accent,
        padding: SPACING.md,
        gap: SPACING.sm,
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}>
        {title}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 18,
          color: colors.textSecondary,
        }}
      >
        {body}
      </Text>
      {onRetry ? (
        <View style={{ alignSelf: 'flex-start' }}>
          <Button title={t('common.retry')} variant="secondary" size="sm" onPress={onRetry} />
        </View>
      ) : null}
    </View>
  )
}
